"use client";

import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import {
  ArrowRight,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "./LanguageProvider";
import {
  HYUNDAI_VARIANTS,
  MAZDA_VARIANTS,
  type CtaContent,
  type VideoVariant,
} from "@/lib/heroVideos";
import { BILSPEKTRUM_FILM, type VideoClip } from "@/lib/bilspektrumVideos";
import { ResponsiveVideo } from "./ResponsiveVideo";

const SLIDE_DURATION_MS = 7000;
/** Long enough for the Bilspektrum film to fly from the sign in through the doors. */
const BILSPEKTRUM_SLIDE_DURATION_MS = 13000;

function pickWeighted(variants: VideoVariant[]): number {
  const total = variants.reduce((sum, v) => sum + v.weight, 0);
  let roll = Math.random() * total;
  for (let i = 0; i < variants.length; i++) {
    roll -= variants[i].weight;
    if (roll <= 0) return i;
  }
  return variants.length - 1;
}

type Slide =
  | { id: string; type: "video"; src: string; cta?: CtaContent }
  | { id: string; type: "clip"; clip: VideoClip }
  | { id: string; type: "image"; src: string };

export const HeroCarousel = () => {
  const [index, setIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [hyundaiVariant, setHyundaiVariant] = useState(0);
  const [mazdaVariant, setMazdaVariant] = useState(0);
  const { language } = useLanguage();

  useEffect(() => {
    setHyundaiVariant(pickWeighted(HYUNDAI_VARIANTS));
    setMazdaVariant(pickWeighted(MAZDA_VARIANTS));
  }, []);

  // Start the hero film after the page has loaded, so the poster (and the rest
  // of the page) isn't competing with a 2–4 MB video on slow phones.
  const [filmReady, setFilmReady] = useState(false);
  useEffect(() => {
    const start = () => setFilmReady(true);
    if (document.readyState === "complete") {
      start();
      return;
    }
    window.addEventListener("load", start, { once: true });
    return () => window.removeEventListener("load", start);
  }, []);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStart === null) return;
    const diff = touchStart - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) goTo(diff > 0 ? 1 : -1);
    setTouchStart(null);
  };

  // Bilspektrum's own film first, then the brands.
  const slides: Slide[] = [
    {
      id: "bilspektrum",
      type: "clip",
      clip: BILSPEKTRUM_FILM,
    },
    {
      id: "mazda",
      type: "video",
      src: MAZDA_VARIANTS[mazdaVariant].src,
      cta: MAZDA_VARIANTS[mazdaVariant].cta,
    },
    {
      id: "hyundai",
      type: "video",
      src: HYUNDAI_VARIANTS[hyundaiVariant].src,
      cta: HYUNDAI_VARIANTS[hyundaiVariant].cta,
    },
  ];

  const goTo = useCallback(
    (next: number) => {
      setIndex((i) => {
        const n = slides.length;
        return (((i + next) % n) + n) % n;
      });
    },
    [slides.length],
  );

  const slideDuration =
    slides[index]?.id === "bilspektrum"
      ? BILSPEKTRUM_SLIDE_DURATION_MS
      : SLIDE_DURATION_MS;

  useEffect(() => {
    if (isPaused) return;
    const t = setTimeout(() => goTo(1), slideDuration);
    return () => clearTimeout(t);
  }, [index, isPaused, goTo, slideDuration]);

  const current = slides[index];
  const isHyundai = current.id === "hyundai" && "cta" in current && current.cta;
  const isBilspektrum = current.id === "bilspektrum";
  const isMazdaSlide =
    current.id === "mazda" && "cta" in current && current.cta;
  const hyundaiCta = isHyundai && "cta" in current ? current.cta : null;
  const mazdaCta = isMazdaSlide && "cta" in current ? current.cta : null;

  return (
    <section
      className="relative min-h-[100dvh] sm:min-h-screen w-full overflow-hidden bg-black touch-pan-y"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocus={() => setIsPaused(true)}
      onBlur={() => setIsPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Slides */}
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={current.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="absolute inset-0"
        >
          {current.type === "clip" ? (
            <>
              {/* Poster as a real image so phones paint it at once (LCP); the video covers it when it plays. */}
              <picture>
                <source
                  media="(max-width: 767px) and (orientation: portrait)"
                  srcSet={current.clip.posterMobile}
                />
                <img
                  src={current.clip.poster}
                  alt=""
                  fetchPriority="high"
                  className="absolute inset-0 w-full h-full object-cover"
                  aria-hidden
                />
              </picture>
              {filmReady && (
                <ResponsiveVideo
                  key={current.clip.desktop}
                  clip={current.clip}
                  autoPlay
                  loop
                  className="absolute inset-0 w-full h-full object-cover"
                  aria-hidden
                />
              )}
            </>
          ) : current.type === "video" ? (
            <video
              key={current.src}
              src={current.src}
              autoPlay
              muted
              loop
              playsInline
              className="absolute inset-0 w-full h-full object-cover"
              aria-hidden
            />
          ) : (
            <Image
              src={current.src}
              alt=""
              fill
              className="object-cover object-center"
              sizes="100vw"
              priority
            />
          )}
          <div
            className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/60"
            aria-hidden
          />
        </motion.div>
      </AnimatePresence>

      {/* Hyundai – overlay + CTA from selected variant */}
      <AnimatePresence>
        {isHyundai && hyundaiCta && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.4 }}
            className="absolute inset-0 z-10 flex flex-col items-center justify-center text-center px-4 sm:px-6 w-full max-w-full min-w-0 box-border"
          >
            <p className="text-white/90 text-xs sm:text-sm tracking-[0.2em] uppercase font-medium mb-2 break-words">
              {language === "sv" ? hyundaiCta.taglineSv : hyundaiCta.taglineEn}
            </p>
            <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white mb-2 sm:mb-3 break-words">
              {hyundaiCta.title}
            </h2>
            {(hyundaiCta.descriptionSv || hyundaiCta.descriptionEn) && (
              <p className="text-white/80 text-sm sm:text-base lg:text-lg max-w-xl mx-auto mb-6 break-words [overflow-wrap:anywhere]">
                {language === "sv"
                  ? hyundaiCta.descriptionSv
                  : hyundaiCta.descriptionEn}
              </p>
            )}
            <Link
              href={hyundaiCta.href}
              className="inline-flex items-center gap-2 px-6 py-3 sm:px-8 sm:py-3.5 bg-hyundai text-white rounded-full font-medium text-sm sm:text-base uppercase tracking-wider hover:bg-hyundai/90 transition-all duration-300 group"
            >
              {language === "sv" ? hyundaiCta.buttonSv : hyundaiCta.buttonEn}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bilspektrum overlay (first slide). initial={false}: visible straight from the server HTML, no fade-in wait (LCP). */}
      <AnimatePresence initial={false}>
        {isBilspektrum && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.4 }}
            className="absolute inset-0 z-10 flex flex-col items-center justify-center text-center px-4 sm:px-6 w-full max-w-full min-w-0 box-border"
          >
            <p className="text-white/90 text-xs sm:text-sm tracking-[0.2em] uppercase font-medium mb-2 break-words">
              {language === "sv" ? "Sedan 1973" : "Since 1973"}
            </p>
            <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white mb-2 sm:mb-3 break-words">
              {language === "sv"
                ? "Välkommen till Bilspektrum AB"
                : "Welcome to Bilspektrum AB"}
            </h1>
            <p className="text-white/80 text-sm sm:text-base lg:text-lg max-w-xl mx-auto mb-6 break-words [overflow-wrap:anywhere]">
              {language === "sv"
                ? "Bilhandel och verkstad i Umeå. Nya och begagnade bilar, service på alla bilmärken med kompetensfokus på Mazda och Hyundai."
                : "Car dealer and workshop in Umeå. New and used cars, service on all car brands with a particular focus on Mazda and Hyundai."}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link
                href="/fordon-i-lager"
                className="inline-flex items-center gap-2 px-6 py-3 sm:px-8 sm:py-3.5 bg-white/90 text-primary rounded-full font-medium text-sm sm:text-base uppercase tracking-wider hover:bg-white transition-all duration-300 group"
              >
                {language === "sv" ? "Bilar i lager" : "Cars in stock"}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                href="/verkstad#boka"
                className="inline-flex items-center gap-2 px-6 py-3 sm:px-8 sm:py-3.5 border border-white/60 text-white rounded-full font-medium text-sm sm:text-base uppercase tracking-wider hover:bg-white/10 transition-all duration-300"
              >
                {language === "sv" ? "Boka service" : "Book service"}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mazda slides – overlay + CTA (6e and CX-60) */}
      <AnimatePresence>
        {isMazdaSlide && mazdaCta && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.4 }}
            className="brand-mazda absolute inset-0 z-10 flex flex-col items-center justify-center text-center px-4 sm:px-6 w-full max-w-full min-w-0 box-border"
          >
            <p className="text-white/90 text-xs sm:text-sm tracking-[0.2em] uppercase font-medium mb-2 break-words">
              {language === "sv" ? mazdaCta.taglineSv : mazdaCta.taglineEn}
            </p>
            <h2 className="mazda-model text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white mb-2 sm:mb-3 break-words">
              {mazdaCta.title}
            </h2>
            {(mazdaCta.descriptionSv || mazdaCta.descriptionEn) && (
              <p className="text-white/80 text-sm sm:text-base lg:text-lg max-w-xl mx-auto mb-6 break-words [overflow-wrap:anywhere]">
                {language === "sv"
                  ? mazdaCta.descriptionSv
                  : mazdaCta.descriptionEn}
              </p>
            )}
            <Link
              href={mazdaCta.href}
              target={mazdaCta.href.startsWith("http") ? "_blank" : undefined}
              rel={
                mazdaCta.href.startsWith("http")
                  ? "noopener noreferrer"
                  : undefined
              }
              className="inline-flex items-center gap-2 px-6 py-3 sm:px-8 sm:py-3.5 bg-mazda text-white rounded-full font-medium text-sm sm:text-base uppercase tracking-wider hover:bg-mazda/90 transition-all duration-300 group"
            >
              {language === "sv" ? mazdaCta.buttonSv : mazdaCta.buttonEn}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Prev/Next – desktop */}
      <div className="hidden sm:flex absolute inset-y-0 left-0 right-0 z-20 pointer-events-none">
        <button
          type="button"
          onClick={() => goTo(-1)}
          className="pointer-events-auto absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors focus:outline-none focus:ring-2 focus:ring-white/50"
          aria-label={language === "sv" ? "Föregående" : "Previous"}
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button
          type="button"
          onClick={() => goTo(1)}
          className="pointer-events-auto absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors focus:outline-none focus:ring-2 focus:ring-white/50"
          aria-label={language === "sv" ? "Nästa" : "Next"}
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>

      {/* Dots – above scroll indicator */}
      <div className="absolute bottom-24 sm:bottom-20 left-0 right-0 z-20 flex justify-center gap-3">
        {slides.map((s, i) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setIndex(i)}
            className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white/70"
            aria-label={
              language === "sv"
                ? `Slide ${i + 1} av ${slides.length}`
                : `Slide ${i + 1} of ${slides.length}`
            }
            aria-current={index === i ? "true" : undefined}
          >
            <span
              className={`block h-2 w-2 sm:h-2.5 sm:w-2.5 rounded-full ${
                index === i
                  ? "bg-white w-6 sm:w-8"
                  : "bg-white/50 hover:bg-white/70"
              }`}
              aria-hidden
            />
          </button>
        ))}
      </div>

      {/* Scroll-down indicator – same as other hero carousels */}
      <button
        type="button"
        onClick={() => {
          window.scrollTo({ top: window.innerHeight, behavior: "smooth" });
        }}
        className="absolute left-1/2 -translate-x-1/2 bottom-4 sm:bottom-5 z-20 flex flex-col items-center gap-1 text-white/90 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white/50 rounded-full p-2"
        aria-label={language === "sv" ? "Scrolla ned" : "Scroll down"}
      >
        <span className="text-xs font-medium uppercase tracking-wider">
          {language === "sv" ? "Scrolla ned" : "Scroll down"}
        </span>
        <ChevronDown className="w-6 h-6 animate-bounce" aria-hidden />
      </button>
    </section>
  );
};
